import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useGetBranches, useGetPaymentMethods } from "src/api/generic";
import { useGetOCAccountsList } from "src/api/operation-cost/accounts";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import {
  useAddOCTransactionMutation,
  useUpdateOCTransactionMutation,
} from "src/api/operation-cost/transactions";
import { LoadingButton } from "@mui/lab";
import FilePondUploader from "src/shared/components/file-pond-uploader";
import { useRecoilState, useRecoilValue } from "recoil";
import { notificationsState } from "src/store/notifications";
import { FormModeTypes } from "src/types/generic";
import { useTranslation } from "react-i18next";
import moment from "moment";
import InfoBox from "src/shared/components/info-box";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import AddMoreIcon from "src/assets/svg-icons/settings/add_more.svg";
import { specificBranchHasPermission } from "src/utils";
import {
  FixedAssetsPermissions,
  OCPermissions,
} from "src/constants/permissions";
import { DatePicker } from "@mui/x-date-pickers";
import { useGetVendorsList } from "src/api/operation-cost/vendors";
import { authState } from "src/store/auth";

const schema = yup.object().shape({
  branch_id: yup.number().required("Required"),
  vendor: yup
    .object()
    .shape({
      id: yup.number(),
    })
    .required("Required"),
  account: yup
    .object()
    .shape({
      id: yup.number(),
    })
    .required("Required"),
  accountType: yup
    .string()
    .required("required")
    .oneOf(["fixed", "variable", "asset"] as const),
  amount: yup
    .string()
    .required("required")
    .test("positive", "mustBeGreaterThanZero", (value) => {
      return Number(value) > 0;
    }),
  include_tax: yup
    .number()
    .required("Required")
    .oneOf([1, 0] as const),
  tax: yup.string().required("Required"),
  invoice_number: yup.string(),
  note: yup.string(),
  payments: yup
    .array()
    .of(
      yup.object().shape({
        payment_method_id: yup.string().test(
          "payment-method-required",
          "required", //Payment method is required when amount is specified
          function (value) {
            const { amount } = this.parent; // Access payment_amount in the same object
            if (amount && !value) {
              return false; // Fail if amount exists but payment method is missing
            }
            return true;
          },
        ),
        amount: yup
          .string()
          .test(
            "payment-amount-required",
            "required", //Payment amount is required when payment method is selected
            function (value) {
              const { payment_method_id } = this.parent; // Access payment_method_id in the same object
              if (payment_method_id && !value) {
                return false; // Fail if payment method exists but amount is missing
              }
              return true;
            },
          )
          .test("min-value", "Must be greater than 0", (value) => {
            if (!value) return true; // Skip validation if the value is empty
            return parseFloat(value) > 0;
          }),
      }),
    )
    .required("Required"),
  // asset account
  deprecation_months: yup.string().when("accountType", {
    is: "asset",
    then: (value) =>
      value
        .required("Required")
        .test(
          "min-value",
          "Must be greater than 0",
          (value) => parseFloat(value) > 0,
        ),
  }),

  due_date: yup.date().when("accountType", {
    is: (value: string) => value === "asset",
    then: (value) => value.required("Required"),
  }),
  from_date: yup
    .date()
    .when("accountType", {
      is: (value: string) => value === "fixed",
      then: (value) => value.required("Required"),
    })
    .when("accountType", {
      is: (value: string) => value === "asset",
      then: (value) =>
        value
          .required("Required")
          .test(
            "is-greater-than-or-equal-due-date",
            "fromDateMustNotBeforeReceivedDate",
            function (value) {
              const { due_date } = this.parent;
              return value >= due_date;
            },
          ),
    }),
  to_date: yup.date().when("accountType", {
    is: (value: string) => value === "fixed" || value === "asset",
    then: (value) =>
      value
        .required("Required")
        .test(
          "is-greater-than-or-equal",
          "toDateMustNotBeforeFromDate",
          function (value) {
            const { from_date } = this.parent;
            return value >= from_date;
          },
        ),
  }),
  // new
  is_received: yup.string().when("accountType", {
    is: (value: string) => value === "fixed" || value === "variable",
    then: (value) => value.required("Required"),
  }),
  bill_date: yup.date().when(["accountType", "is_received"], {
    is: (accountType: string, is_received: string) =>
      accountType === "variable" ||
      accountType === "asset" ||
      (accountType === "fixed" && is_received === "yes"),
    then: (value) => value.required("Required"),
  }),
  payment_due_date: yup.date(),
});

export interface FormInputs extends yup.InferType<typeof schema> {}

interface IProps {
  open: boolean;
  handleClose: () => void;
  row?: any;
  refetch: Function;
  isAssetsOnly?: boolean;
  receivedBill?: boolean;
}

const AddEditTransactionPopup = (props: IProps) => {
  const { open, handleClose, row, refetch, isAssetsOnly, receivedBill } = props;
  const { t } = useTranslation("operationCost");
  const { t: generalT } = useTranslation("general");
  const { t: purchasingT } = useTranslation("purchasing");
  const mode: FormModeTypes = row ? "edit" : "add";
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [files, setFiles] = useState<any>([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [resetIsDone, setResetIsDone] = useState(false);
  const auth = useRecoilValue(authState);
  const user = auth?.user;
  const company = user?.company;

  //RHF
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
    reset,
  } = useForm<FormInputs>({
    mode: "all",
    resolver: yupResolver(schema),
    defaultValues: {
      tax: "0",
      include_tax: 1,
      payments: [
        {
          payment_method_id: "",
          amount: "",
        },
      ],
      is_received: "yes",
    },
  });

  const { fields, append, remove } = useFieldArray({
    name: "payments",
    control,
  });

  const accountType = watch("accountType");
  const isReceived = watch("is_received");

  //APIS
  const { data: branchesData } = useGetBranches();
  const { data, isLoading } = useGetOCAccountsList({
    ...(isAssetsOnly || row?.type === "asset" ? { is_asset: 1 } : {}),
  });
  const { data: vendorsData, isLoading: isLoadingVendors } =
    useGetVendorsList();
  const { data: paymentsData } = useGetPaymentMethods({
    enabled: !!watch("branch_id"),
    branches: [watch("branch_id")],
  });
  const { mutate, status, isPending, error } = useAddOCTransactionMutation();
  const {
    mutate: mutateUpdate,
    status: statusUpdate,
    error: errorUpdate,
    isPending: isPendingUpdate,
  } = useUpdateOCTransactionMutation();

  // permissions
  const pagePermissionsType = isAssetsOnly
    ? FixedAssetsPermissions.viewFixedAssetBills
    : OCPermissions.viewBills;
  const branchesHavePermission =
    branchesData?.data.filter(
      (branch) =>
        branch.status &&
        specificBranchHasPermission(pagePermissionsType, branch.id),
    ) || [];

  const amountWithoutTax =
    +watch("include_tax") === 1
      ? +(+watch("amount") - +watch("tax")).toFixed(5)
      : +watch("amount");

  const amountWithTax =
    +watch("include_tax") === 1
      ? +(+watch("amount")).toFixed(5)
      : +(+watch("amount") + +watch("tax")).toFixed(5);

  let paymentsCredit = 0;
  watch("payments").forEach((pay) => {
    paymentsCredit += Number(pay.amount) || 0;
  });
  const credit = +(amountWithTax - paymentsCredit) || 0;

  const handleAppendPayment = () => {
    append({
      payment_method_id: "",
      amount: "",
    });
  };

  const handleRemovePayment = (index: number) => {
    remove(index);
  };

  const onClose = () => {
    handleClose();
    reset();
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };

  const isReminderInvoice = !row?.date;

  const isAssetInvoice = row?.type === "asset";

  const isCopyOfDeprecationAssetInvoice =
    isAssetInvoice && !!row?.parent_invoice;

  const selectedBranch = branchesData?.data.find((branch) => {
    return branch.id === +watch("branch_id");
  });
  const taxPercentage = (selectedBranch?.vat ?? 0) / 100;
  const taxDivider = taxPercentage + 1;

  const numberOfDeprecationMonths = Number(watch("deprecation_months")) || 0;

  const depPercent = (1 / numberOfDeprecationMonths) * 100;

  const depAmount = (amountWithoutTax / numberOfDeprecationMonths).toFixed(2);

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    // if (
    //   +data.is_payment_advanced === 1 &&
    //   data.purchase_event_amount &&
    //   +data.purchase_event_amount > totalPurchaseEventCreditRemaining
    // ) {
    //   setNotifications([
    //     ...notifications,
    //     {
    //       type: "error",
    //       message: `${t("eventBalance")}: ${totalPurchaseEventCreditRemaining}`,
    //     },
    //   ]);
    //   return;
    // }

    // Balance date validation
    if (
      !!company?.start_balance_date &&
      moment(watch("due_date"))
        .startOf("day")
        .isBefore(moment(company.start_balance_date).startOf("day"))
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: `${generalT("balanceDateValidationMsg")}: ${
            company?.start_balance_date
          }`,
        },
      ]);
      return;
    }

    // Lock date validation
    if (
      !!selectedBranch?.close_date &&
      moment(watch("due_date")).isSameOrBefore(
        moment(selectedBranch.close_date),
      )
    ) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: `${generalT("canNotAddEditOrDeleteBeforeLockDate")}: ${
            selectedBranch?.close_date
          }`,
        },
      ]);
      return;
    }

    if (mode === "edit" && row?.parent_invoice && !isAssetInvoice) {
      if (!moment(watch("from_date")).isAfter(row.parent_invoice.end_date)) {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message: `${t("lastCostDisruptionWas")} ${
              row?.parent_invoice.date
            } ${generalT("to")} ${row?.parent_invoice.end_date}, ${t(
              "pickupAfterThisDate",
            )} ${row?.parent_invoice.end_date}`,
          },
        ]);
        return;
      }
    }

    console.log({ data });
    const {
      tax,
      note,
      invoice_number,
      payments,
      include_tax,
      branch_id,
      vendor,
      account,
      deprecation_months,
      accountType,
      due_date,
      from_date,
      to_date,
      is_received,
      bill_date,
      payment_due_date,
    } = data;

    const dataToSend: any = {
      branch_id: branch_id,
      account_id: account.id,
      vendor_id: vendor.id,
      // amount: +include_tax === 1 ? +(+amount - +tax).toFixed(5) : +amount,
      amount: amountWithoutTax,
      tax: tax,
      ...(invoice_number && {
        invoice_number: +invoice_number,
      }),
      include_tax: include_tax,
      ...(note && { note: note }),
      // ...(accountType === "fixed" && {
      //   is_advanced: Number(cost_type),
      // }),

      ...(accountType === "asset" && {
        due_date: moment(due_date).format("YYYY-MM-DD"),
      }),

      // ...(accountType === "fixed" &&
      //   !!remind_date && {
      //     repeat_date: moment(remind_date).format("YYYY-MM-DD"),
      //   }),
      ...((accountType === "fixed" || accountType === "asset") && {
        date: moment(from_date).format("YYYY-MM-DD"),
        end_date: moment(to_date).format("YYYY-MM-DD"),
      }),
      // asset account
      ...(accountType === "asset" && {
        deprecation_months: deprecation_months,
        deprecation_amount: depAmount,
        deprecation_percentage: depPercent,
      }),

      // new
      ...((accountType === "fixed" || accountType === "variable") && {
        is_received: is_received === "yes" ? 1 : 0,
      }),

      ...(((accountType === "fixed" && isReceived === "yes") ||
        accountType === "variable" ||
        accountType === "asset") && {
        bill_date: moment(bill_date).format("YYYY-MM-DD"),
      }),

      ...((accountType === "fixed" || accountType === "variable") &&
        is_received === "yes" &&
        !!payment_due_date && {
          payment_due_date: moment(payment_due_date).format("YYYY-MM-DD"),
        }),
    };
    console.log({ dataToSend });
    const formData = new FormData();
    // append data
    for (const key in dataToSend) {
      formData.append(key, dataToSend[key]);
    }
    // append files
    if (files.length > 0) {
      files.forEach((file: any, index: number) => {
        formData.append(`files[${index}]`, file);
      });
    }
    if (mode === "add") {
      // append payments array
      payments.forEach((payment, index: number) => {
        if (
          payment.payment_method_id &&
          payment.amount &&
          +payment?.amount > 0
        ) {
          formData.append(
            `payments[${index}][payment_method_id]`,
            payment.payment_method_id,
          );
          formData.append(`payments[${index}][amount]`, payment.amount);
        }
      });
      mutate(formData);
    } else {
      mutateUpdate({ ...dataToSend, id: row.id, payments: payments });
    }
  };

  useEffect(() => {
    if (row && data) {
      const selectedAccount = data?.data?.find(
        (account: any) => account.id === row.account_id,
      );
      const selectedVendor = vendorsData?.data?.find(
        (vendor: any) => vendor.id === row.vendor_id,
      );
      reset({
        branch_id: row.branch_id,
        vendor: selectedVendor,
        account: selectedAccount,
        amount: row.include_tax ? row.total : row.amount,
        tax: !row.is_received ? "0" : (row.tax ?? ""),
        include_tax: row.include_tax ? 1 : 0,
        invoice_number: row.invoice_number || "",
        payments: row.payments.map((pay: any) => {
          return {
            payment_method_id: pay.payment_method_id,
            amount: pay.total,
          };
        }),
        note: row.note || "",
        due_date: row.due_date
          ? moment(row.due_date, "YYYY-MM-DD").toDate()
          : undefined,
        from_date: row.date
          ? moment(row.date, "YYYY-MM-DD").toDate()
          : undefined,
        to_date: row.end_date
          ? moment(row.end_date, "YYYY-MM-DD").toDate()
          : undefined,

        deprecation_months: row.deprecation_months || "",
        is_received: row.is_received ? "yes" : "no",
        bill_date:
          row.bill_date && row.bill_date !== "Waiting"
            ? moment(row.bill_date, "YYYY-MM-DD").toDate()
            : undefined,
        payment_due_date: row.payment_due_date
          ? moment(row.payment_due_date, "YYYY-MM-DD").toDate()
          : undefined,
      });
      setValue(
        "accountType",
        selectedAccount?.type === "fixed"
          ? "fixed"
          : selectedAccount?.type === "asset"
            ? "asset"
            : "variable",
      );

      setTimeout(() => {
        setResetIsDone(true);
      }, 1000);
    }
  }, [row, open, data]);

  useEffect(() => {
    if ((mode === "edit" && !resetIsDone) || isReceived === "no") return;
    if (+watch("include_tax") === 1) {
      setValue(
        "tax",
        `${(((+getValues("amount") || 0) / taxDivider) * taxPercentage).toFixed(
          2,
        )}`,
      );
    } else if (+watch("include_tax") === 0) {
      setValue(
        "tax",
        `${((+getValues("amount") || 0) * taxPercentage).toFixed(2)}`,
      );
    }
  }, [watch("include_tax"), watch("amount"), isReceived]);

  useEffect(() => {
    if (watch("account")) {
      const selectedAccount = data?.data?.find(
        (account: any) => account.id === watch("account").id,
      );
      console.log({ selectedAccount });
      setValue("accountType", selectedAccount?.type);
    }
  }, [watch("account"), data]);

  // useEffect(() => {
  //   if (accountEventsData && !!watch("purchase_event_id") && mode === "add") {
  //     let purchaseEventAmount =
  //       accountEventsData?.data.find(
  //         (item: any) => item.id === Number(watch("purchase_event_id"))
  //       )?.remaining ?? 0;
  //     setValue("purchase_event_amount", purchaseEventAmount);
  //     console.log({ credit });
  //   }
  // }, [watch("purchase_event_id"), accountEventsData]);

  //Add OC trans success and error handling
  useEffect(() => {
    if (status === "success") {
      refetch();
      onClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("addedSuccessfully"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  //Update OC trans success and error handling
  useEffect(() => {
    if (statusUpdate === "success") {
      refetch();
      onClose();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("updatedSuccessfully"),
        },
      ]);
    } else if (statusUpdate === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            errorUpdate?.data.errors?.[0].value || generalT("somethingWrong"),
        },
      ]);
    }
  }, [statusUpdate]);

  console.log({ errors }, resetIsDone, getValues());
  console.log({ row });

  useEffect(() => {
    if (
      mode === "add" &&
      watch("accountType") === "asset" &&
      !!watch("from_date") &&
      Number(watch("deprecation_months")) > 0
    ) {
      const endDateOfDeprecation = moment(watch("from_date"))
        .add(numberOfDeprecationMonths, "months")
        .subtract(1, "day")
        .toDate();
      setValue("to_date", endDateOfDeprecation);
    }
  }, [watch("deprecation_months"), watch("from_date")]);

  if (isLoading || isLoadingVendors) {
    return <></>;
  }

  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Stack
        spacing={3}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "100%", sm: 600 },
          bgcolor: "#FFF",
          border: "1px solid #D0D5DD",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          p={0}
        >
          <Typography color={"#101828"} fontSize={"20px"}>
            {mode === "add" ? t("addNewExpense") : t("editExpense")}
          </Typography>
          <IconButton onClick={handleOpenConfirm} sx={{ p: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            {/* branch */}
            <FormControl fullWidth size="small" error={!!errors.branch_id}>
              <InputLabel id="demo-simple-select-label">
                {generalT("branch")}
              </InputLabel>
              <Controller
                name="branch_id"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    key={field.value}
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    label={generalT("branch")}
                    disabled={mode === "edit"}
                  >
                    {branchesHavePermission?.map(
                      ({ id, name, active_subscriptions, is_new }) => {
                        const branchRequiresPackageRenew =
                          !active_subscriptions && !is_new;
                        return (
                          <MenuItem
                            key={id}
                            value={id}
                            disabled={branchRequiresPackageRenew}
                          >
                            {name}
                            {branchRequiresPackageRenew && (
                              <Typography
                                fontWeight={700}
                                color={"#ff0000ff"}
                                mx={0.5}
                                fontSize={"15px"}
                                sx={{
                                  opacity: "1 !important",
                                  fontStyle: "italic",
                                }}
                              >
                                ({generalT("branchPackageNeedsRenewed")})
                              </Typography>
                            )}
                          </MenuItem>
                        );
                      },
                    )}
                  </Select>
                )}
              />
              <FormHelperText id="my-helper-text">
                {errors.branch_id?.message}
              </FormHelperText>
            </FormControl>

            {/* Vendor */}
            <FormControl fullWidth size="small" error={!!errors.vendor}>
              <Controller
                name="vendor"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    size="small"
                    {...field}
                    options={vendorsData?.data ?? []}
                    disabled={row && row.is_editable_vendor === false}
                    getOptionLabel={(option: any) => option?.name}
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        error={!!errors.vendor}
                        helperText={errors.vendor?.message}
                        label={t("selectVendor")}
                      />
                    )}
                    renderOption={(props, option: any, { selected }) => (
                      <li key={props.id} {...props}>
                        <Typography>{option.name}</Typography>
                      </li>
                    )}
                    onChange={(_, data) => {
                      field.onChange(data);
                    }}
                  />
                )}
              />
            </FormControl>

            {/* Account */}
            <FormControl fullWidth size="small" error={!!errors.account}>
              <Controller
                name="account"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    size="small"
                    {...field}
                    options={data?.data?.filter(
                      (item: any) =>
                        (!item.has_invoices ||
                          item.type === "variable" ||
                          item.type === "fixed" ||
                          row?.account_id === item.id) &&
                        item.status,
                    )}
                    getOptionLabel={(option: any) => option?.name}
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        error={!!errors.account}
                        helperText={errors.account?.message}
                        label={t("selectAccount")}
                      />
                    )}
                    renderOption={(props, option: any, { selected }) => (
                      <li key={props.id} {...props}>
                        <Typography>{option.name}</Typography>
                      </li>
                    )}
                    onChange={(_, data) => {
                      field.onChange(data);
                    }}
                    disabled={mode === "edit"}
                  />
                )}
              />
            </FormControl>

            {watch("accountType") === "fixed" ||
            watch("accountType") === "variable" ? (
              <Stack direction={"row"} gap={3} alignItems={"flex-end"}>
                <Typography>{t("didYouReceiveBill")}</Typography>
                <Box>
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.is_received}
                  >
                    <Controller
                      name="is_received"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value);
                            if (value === "no") {
                              setValue("tax", "0");
                              setValue("include_tax", 0);
                            }
                          }}
                          key={field.value}
                          row
                          aria-labelledby="demo-row-radio-buttons-group-label"
                          name="row-radio-buttons-group"
                        >
                          <FormControlLabel
                            value="yes"
                            control={
                              <Radio
                                size="small"
                                icon={<CircleOutlinedIcon />}
                                checkedIcon={<CheckCircleIcon />}
                              />
                            }
                            label={
                              <Typography fontSize={"14px"}>
                                {generalT("yes")}
                              </Typography>
                            }
                            sx={{ color: "#98A2B3" }}
                            disabled={
                              isAssetInvoice ||
                              (!receivedBill &&
                                isReceived === "no" &&
                                mode === "edit")
                            }
                          />
                          <FormControlLabel
                            value="no"
                            control={
                              <Radio
                                size="small"
                                icon={<CircleOutlinedIcon />}
                                checkedIcon={<CheckCircleIcon />}
                              />
                            }
                            label={
                              <Typography fontSize={"14px"}>
                                {generalT("no")}
                              </Typography>
                            }
                            sx={{ color: "#98A2B3" }}
                            disabled={
                              isAssetInvoice ||
                              (mode === "edit" && row.is_received) // is received already so can't be no
                            }
                          />
                        </RadioGroup>
                      )}
                    />
                  </FormControl>
                  <FormHelperText
                    error
                    id="my-helper-text"
                    sx={{ mt: -1, px: 2 }}
                  >
                    {errors.is_received?.message}
                  </FormHelperText>
                </Box>
              </Stack>
            ) : null}

            {/* Amount */}
            <Stack direction={"row"} alignItems={"flex-start"} gap={1}>
              <Box
                sx={{
                  width:
                    accountType === "asset" || isReceived === "yes"
                      ? 220
                      : "100%",
                }}
              >
                <Controller
                  name="amount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      fullWidth
                      label={t(
                        accountType === "asset" || isReceived === "yes"
                          ? "amount"
                          : "expectedAmountWithoutTax",
                      )}
                      size="small"
                      {...field}
                      error={!!errors.amount}
                      helperText={generalT(errors.amount?.message ?? "")}
                      disabled={
                        isAssetInvoice ||
                        (receivedBill && isReceived === "no") ||
                        (receivedBill && watch("accountType") === "fixed")
                      }
                    />
                  )}
                />
              </Box>
              {accountType === "asset" || isReceived === "yes" ? (
                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.include_tax}
                  sx={{ width: "fit-content", ml: { xs: "auto", sm: "unset" } }}
                >
                  <Controller
                    name="include_tax"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        {...field}
                        key={field.value}
                        row
                        aria-labelledby="demo-row-radio-buttons-group-label"
                        name="row-radio-buttons-group"
                      >
                        <FormControlLabel
                          value="1"
                          control={
                            <Radio
                              size="small"
                              icon={<CircleOutlinedIcon />}
                              checkedIcon={<CheckCircleIcon />}
                            />
                          }
                          label={generalT("includeTax")}
                          sx={{ color: "#98A2B3" }}
                          disabled={isAssetInvoice}
                        />
                        <FormControlLabel
                          value="0"
                          control={
                            <Radio
                              size="small"
                              icon={<CircleOutlinedIcon />}
                              checkedIcon={<CheckCircleIcon />}
                            />
                          }
                          label={generalT("excludeTax")}
                          sx={{ color: "#98A2B3" }}
                          disabled={isAssetInvoice}
                        />
                      </RadioGroup>
                    )}
                  />
                  <FormHelperText id="my-helper-text">
                    {errors.include_tax?.message}
                  </FormHelperText>
                </FormControl>
              ) : null}
            </Stack>
            {/* tax */}
            {accountType === "asset" || isReceived === "yes" ? (
              <Controller
                name="tax"
                control={control}
                render={({ field }) => (
                  <TextField
                    label={t("taxPutZero")}
                    fullWidth
                    size="small"
                    {...field}
                    error={!!errors.tax}
                    helperText={errors.tax?.message}
                    disabled={isAssetInvoice}
                  />
                )}
              />
            ) : null}
            {watch("accountType") === "asset" && (
              <Stack direction={"row"} gap={2} alignItems={"center"}>
                <Controller
                  name="deprecation_months"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      label={t("depreciationPeriodMonths")}
                      fullWidth
                      size="small"
                      {...field}
                      error={!!errors.deprecation_months}
                      helperText={errors.deprecation_months?.message}
                      sx={{ flexBasis: "60%" }}
                      disabled={isAssetInvoice}
                    />
                  )}
                />
                <Typography>
                  <Typography component={"span"} color={"textSecondary"}>
                    Amount
                  </Typography>{" "}
                  : {depAmount} SAR
                </Typography>
              </Stack>
            )}

            <Stack direction={"row"} gap={2}>
              {/* invoice number */}
              {accountType === "asset" || isReceived === "yes" ? (
                <Controller
                  name="invoice_number"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label={t("billNumberOptional")}
                      fullWidth
                      size="small"
                      {...field}
                      error={!!errors.invoice_number}
                      helperText={errors.invoice_number?.message}
                    />
                  )}
                  disabled={isAssetInvoice}
                />
              ) : null}
              {/* bill date */}
              {(accountType === "fixed" && isReceived === "yes") ||
              accountType === "variable" ? (
                <Controller
                  name="bill_date"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.bill_date,
                          helperText: t(`${errors.bill_date?.message ?? ""}`),
                          size: "small",
                          inputProps: {
                            readOnly: true,
                            onKeyDownCapture: (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            },
                            onPasteCapture: (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            },
                          },
                        },
                      }}
                      format={"dd/MM/yyyy"}
                      label={`${t(isReceived === "no" ? "accruedDate" : "billDate")}`}
                      disabled={
                        isAssetInvoice || (receivedBill && isReceived === "no")
                      }
                    />
                  )}
                />
              ) : null}
            </Stack>

            {accountType === "asset" || isReceived === "yes" ? (
              <>
                {/* payments */}
                {fields.map((paymentField, index) => {
                  return (
                    <Stack key={paymentField.id} direction={"row"} gap={1}>
                      <FormControl
                        fullWidth
                        size="small"
                        error={
                          errors.payments &&
                          !!errors.payments[index]?.payment_method_id
                        }
                      >
                        <InputLabel id="demo-simple-select-label">
                          {t("paymentMethodOptional")}
                        </InputLabel>
                        <Controller
                          name={`payments.${index}.payment_method_id`}
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              key={field.value}
                              labelId="demo-simple-select-label"
                              id="demo-simple-select"
                              label={t("paymentMethodOptional")}
                              disabled={mode === "edit"}
                            >
                              {paymentsData?.data
                                ?.filter((pay) => pay.status)
                                .map(({ id, name }) => {
                                  return (
                                    <MenuItem key={id} value={`${id}`}>
                                      {name}
                                    </MenuItem>
                                  );
                                })}
                            </Select>
                          )}
                        />
                        <FormHelperText id="my-helper-text">
                          {errors.payments?.[index]?.payment_method_id?.message}
                        </FormHelperText>
                      </FormControl>

                      <Controller
                        name={`payments.${index}.amount`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            label={t("amount")}
                            variant="outlined"
                            {...field}
                            error={
                              errors.payments &&
                              !!errors.payments[index]?.amount
                            }
                            helperText={
                              errors.payments?.[index]?.amount?.message
                            }
                            fullWidth
                            size="small"
                            InputProps={{
                              inputProps: { min: 0 },
                            }}
                            disabled={mode === "edit"}
                          />
                        )}
                      />

                      {mode === "add" && (
                        <IconButton onClick={() => handleRemovePayment(index)}>
                          <img src={DeleteIcon} alt="" />
                        </IconButton>
                      )}
                    </Stack>
                  );
                })}

                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  {mode === "add" && (
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<img src={AddMoreIcon} alt="" />}
                      onClick={handleAppendPayment}
                      sx={{ alignSelf: "flex-start" }}
                    >
                      {purchasingT("addAnotherMethod")}
                    </Button>
                  )}

                  <Typography>
                    {generalT("remainingAmount")}: {credit} {generalT("sar")}
                  </Typography>
                </Stack>

                {isReceived === "yes" && accountType !== "asset" && (
                  <Controller
                    name="payment_due_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!errors.payment_due_date,
                            helperText: t(
                              `${errors.payment_due_date?.message ?? ""}`,
                            ),
                            size: "small",
                            inputProps: {
                              readOnly: true,
                              onKeyDownCapture: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              },
                              onPasteCapture: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              },
                            },
                          },
                        }}
                        format={"dd/MM/yyyy"}
                        label={`${t("paymentDueDate")}`}
                        disabled={isAssetInvoice}
                      />
                    )}
                  />
                )}
              </>
            ) : null}
          </Stack>

          {mode === "edit" && row?.parent_invoice && !isAssetInvoice ? (
            <Box mt={2}>
              <InfoBox
                text={`${t("lastCostDisruptionWas")} ${
                  row?.parent_invoice.date
                } ${generalT("to")} ${row?.parent_invoice.end_date}, ${t(
                  "pickupAfterThisDate",
                )} ${row?.parent_invoice.end_date}`}
              />
            </Box>
          ) : null}

          {watch("accountType") === "asset" ? (
            <Stack direction={"row"} gap={2} mt={2}>
              <Controller
                name="bill_date"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.bill_date,
                        helperText: t(`${errors.bill_date?.message ?? ""}`),
                        size: "small",
                        inputProps: {
                          readOnly: true,
                          onKeyDownCapture: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          },
                          onPasteCapture: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          },
                        },
                      },
                    }}
                    format={"dd/MM/yyyy"}
                    label={`${t("billDate")}`}
                    disabled={isAssetInvoice}
                  />
                )}
              />

              <Controller
                name="due_date"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.due_date,
                        helperText: t(`${errors.due_date?.message ?? ""}`),
                        size: "small",
                        inputProps: {
                          readOnly: true,
                          onKeyDownCapture: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          },
                          onPasteCapture: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          },
                        },
                        InputLabelProps: {
                          shrink: true,
                        },
                      },
                    }}
                    format={"dd/MM/yyyy"}
                    label={`${generalT("receivedDate")} (${t("whenBillDue")})`}
                    disabled={isAssetInvoice}
                  />
                )}
              />
            </Stack>
          ) : null}

          {accountType === "asset" || accountType === "fixed" ? (
            <Stack mt={2} gap={2}>
              <Typography fontSize={"14px"} fontWeight={600}>
                {watch("accountType") === "fixed"
                  ? t("servicePeriod")
                  : t("deprecationPeriod")}
              </Typography>

              <Stack direction={"row"} gap={2}>
                <Controller
                  name="from_date"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.from_date,
                          helperText: t(`${errors.from_date?.message ?? ""}`),
                          size: "small",
                          inputProps: {
                            readOnly: true,
                            onKeyDownCapture: (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            },
                            onPasteCapture: (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            },
                          },
                        },
                      }}
                      format={"dd/MM/yyyy"}
                      label={`${t("fromDate")}`}
                      disabled={
                        isAssetInvoice ||
                        (receivedBill && watch("accountType") === "fixed")
                      }
                    />
                  )}
                />

                <Controller
                  name="to_date"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.to_date,
                          helperText: t(`${errors.to_date?.message ?? ""}`),
                          size: "small",
                          inputProps: {
                            readOnly: true,
                            onKeyDownCapture: (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            },
                            onPasteCapture: (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            },
                          },
                        },
                      }}
                      format={"dd/MM/yyyy"}
                      label={`${t("toDate")}`}
                      disabled={
                        isAssetInvoice ||
                        watch("accountType") === "asset" ||
                        (receivedBill && watch("accountType") === "fixed")
                      }
                    />
                  )}
                />
              </Stack>

              {watch("accountType") === "asset" && watch("to_date") && (
                <InfoBox
                  text={`${t(
                    "billWillAutoDeprecationMonthlyUntil",
                  )} ${moment(watch("to_date")).format("DD/MM/YYYY")}`}
                />
              )}
            </Stack>
          ) : null}

          {/* note */}
          <Controller
            name="note"
            control={control}
            render={({ field }) => (
              <TextField
                label={t("notesOptional")}
                fullWidth
                size="small"
                multiline
                rows={2}
                maxRows={3}
                {...field}
                error={!!errors.note}
                helperText={errors.note?.message}
                sx={{ mt: 2 }}
                disabled={receivedBill && isReceived === "no"}
              />
            )}
          />

          {/* files */}
          {mode === "add" && (
            <Box width={"100%"} mt={2}>
              <FilePondUploader
                onUpload={(e) => setFiles(e)}
                maxFiles={3}
                maxFileSize={3}
                acceptedFileTypes={[
                  "image/*",
                  "application/pdf",
                  "text/csv",
                  "application/vnd.ms-excel",
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                ]}
              />
            </Box>
          )}
          <Stack spacing={2} direction={"row"} mt={3}>
            <Button
              variant="outlined"
              color="tertiary"
              fullWidth
              onClick={handleOpenConfirm}
            >
              {generalT("cancel")}
            </Button>
            <LoadingButton
              variant="contained"
              fullWidth
              type="submit"
              loading={isPending || isPendingUpdate}
            >
              {generalT("save")}
            </LoadingButton>
          </Stack>
        </form>
        {/* confirm close popup */}
        <ConfirmPopup
          open={openConfirm}
          handleClose={handleCloseConfirm}
          handleConfirm={handleClose}
          title={generalT("discard")}
          subtitle={generalT("areYouSureToDiscardChanges")}
          confirmBtnText={generalT("confirm")}
        />
      </Stack>
    </Modal>
  );
};

export default AddEditTransactionPopup;
